/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowUpRight, ArrowRight, WarningCircle, Bell, FileText, ShieldCheck,
  Buildings, Storefront, FolderSimple, Package,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import HaleConsoleLayout from '../components/hale/HaleConsoleLayout';
import { T, FONT, ConsoleCard, Eyebrow, StatusPill, Spinner } from './hale-console/_theme';

const API = process.env.REACT_APP_BACKEND_URL;

const REVIEW_STATUS = (s) =>
  ['approved', 'approved_with_recommendations', 'not_approved'].includes(s) ? 'complete'
  : s === 'pending_review' ? 'pending_review'
  : s === 'pending_revision' ? 'pending_revision'
  : 'not_started';

const SECTIONS = [
  { icon: Package,            title: 'My Products',       desc: 'Submit & track Hale Reviewed products',  to: '/hale/products' },
  { icon: Buildings,          title: 'My Brand',          desc: 'Company profile, contacts, portfolio', to: '/hale/profile' },
  { icon: FileText,           title: 'Applications',      desc: 'Application status & outcome',          to: '/hale/applications' },
  { icon: ShieldCheck,        title: 'Scientific Review', desc: 'Reviews & requests for information',    to: '/hale/reviews' },
  { icon: FolderSimple,       title: 'Documents',         desc: 'Files, reports, certificates',          to: '/hale/documents' },
  { icon: Storefront,         title: 'Marketplace',       desc: 'Approved partner resources & network',  to: '/hale/marketplace' },
];

export default function HaleDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/api/hale/dashboard`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/hale/login', { replace: true }); return; }
      setError(e?.response?.data?.detail || 'Could not load your Hale dashboard right now.');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const appStatus = data?.application?.status || 'not_started';
  const reviewStatus = REVIEW_STATUS(appStatus);

  const notifications = [];
  if (data?.next_action?.label) notifications.push({ text: data.next_action.label, to: data.next_action.href });
  if (data?.application?.submitted_at) notifications.push({ text: `Application submitted on ${new Date(data.application.submitted_at).toLocaleDateString()}.` });
  if (reviewStatus === 'complete') notifications.push({ text: 'Your scientific review decision is available.', to: '/hale/reviews' });

  const Tile = ({ label, children, testid }) => (
    <ConsoleCard testid={testid} className="!p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>{label}</p>
      {children}
    </ConsoleCard>
  );

  return (
    <HaleConsoleLayout>
      <Helmet><title>Brand Dashboard · The Hale League</title></Helmet>

      <div className="mb-8">
        <Eyebrow>The Hale League</Eyebrow>
        <h1 className="text-[clamp(1.8rem,3.4vw,2.5rem)] font-black tracking-[-0.02em] leading-[1.08]" style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid="hale-dashboard-title">
          Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
        </h1>
        <p className="mt-3 text-[14px]" style={{ color: T.MUTED }}>Your application, scientific review, documents, and marketplace access — in one place.</p>
      </div>

      {loading && <Spinner label="Loading your dashboard…" />}

      {error && !loading && (
        <ConsoleCard className="flex items-start gap-3" style={{ borderColor: 'rgba(185,28,28,0.35)', backgroundColor: 'rgba(185,28,28,0.04)' }}>
          <WarningCircle size={20} weight="fill" style={{ color: '#B91C1C' }} className="mt-0.5" />
          <p className="text-[14px] font-semibold" style={{ color: '#B91C1C' }} data-testid="hale-dash-error">{error}</p>
        </ConsoleCard>
      )}

      {data && !loading && (
        <>
          {/* Overview tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="hale-overview-tiles">
            <Tile label="Application status" testid="hale-tile-application">
              <StatusPill status={appStatus} />
            </Tile>
            <Tile label="Scientific review" testid="hale-tile-review">
              <StatusPill status={reviewStatus} />
            </Tile>
            <Tile label="SKU portfolio" testid="hale-tile-portfolio">
              <p className="text-[26px] font-black leading-none tabular-nums" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{data.portfolio?.total_skus ?? 0}</p>
            </Tile>
            <Tile label="Notifications" testid="hale-tile-notifications">
              <p className="text-[26px] font-black leading-none tabular-nums" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{notifications.length}</p>
            </Tile>
          </div>

          {/* Next action */}
          {data.next_action?.label && (
            <div className="rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6" style={{ backgroundColor: T.INK, color: T.WHITE }} data-testid="hale-dash-next-action">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: T.RED, fontFamily: FONT.MONO }}>Recommended next action</p>
                <p className="text-[18px] font-bold tracking-tight" style={{ fontFamily: FONT.DISPLAY }}>{data.next_action.label}</p>
              </div>
              <Link to={data.next_action.href} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-[13px] font-semibold w-full md:w-auto" style={{ backgroundColor: T.RED, color: T.WHITE }} data-testid="hale-dash-next-cta">
                Take action <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Notifications */}
            <ConsoleCard className="lg:col-span-1" testid="hale-dash-notifications">
              <div className="flex items-center gap-2 mb-4">
                <Bell size={16} weight="bold" style={{ color: T.RED }} />
                <p className="text-[14px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>Notifications</p>
              </div>
              {notifications.length > 0 ? (
                <ul className="space-y-3">
                  {notifications.map((n, i) => (
                    <li key={i} className="text-[13px] leading-snug" style={{ color: T.SOFT }}>
                      {n.to ? <Link to={n.to} className="hover:underline">{n.text}</Link> : n.text}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[13px]" style={{ color: T.MUTED }}>You&apos;re all caught up.</p>}
              {data.reviewer_contact && (
                <p className="mt-5 pt-4 border-t text-[12px]" style={{ borderColor: T.LINE, color: T.MUTED }}>
                  Reviewer · <a href={`mailto:${data.reviewer_contact}`} className="underline" style={{ color: T.RED }}>{data.reviewer_contact}</a>
                </p>
              )}
            </ConsoleCard>

            {/* Section quick access */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="hale-dash-sections">
              {SECTIONS.map((s) => (
                <Link key={s.title} to={s.to} className="rounded-xl border p-5 transition-shadow hover:shadow-md" style={{ borderColor: T.LINE, backgroundColor: T.WHITE }} data-testid={`hale-section-${s.title.toLowerCase().split(' ')[0]}`}>
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg mb-3" style={{ backgroundColor: 'rgba(230,57,70,0.10)' }}>
                    <s.icon size={20} weight="bold" style={{ color: T.RED }} />
                  </span>
                  <p className="text-[15px] font-bold mb-1 flex items-center gap-1.5" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>
                    {s.title} <ArrowRight size={13} weight="bold" style={{ color: T.MUTED }} />
                  </p>
                  <p className="text-[12.5px]" style={{ color: T.MUTED }}>{s.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </HaleConsoleLayout>
  );
}
